import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { deleteAndValidate } from "~/shared/services/api/fetch-client.ts";

export const unlockShipment = async ({
	poNumber,
	keepalive,
}: {
	poNumber: string;
	keepalive?: boolean;
}) => {
	const url = apiRoutes.UNLOCKSHIPMENT.replace(":poNumber", poNumber);
	const resp = await deleteAndValidate(url, z.unknown(), { keepalive });

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const useUnlockShipment = ({
	showNotifications = true,
}: {
	showNotifications?: boolean;
} = {}) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		string
	>({
		mutationFn: (poNumber: string) => {
			if (!poNumber) {
				throw new Error("PO Number is required");
			}
			return unlockShipment({ poNumber });
		},
		onSuccess: () => {
			if (showNotifications) {
				showSuccess("Shipment unlocked.");
			}
			void queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
		onError: (err) => {
			if (showNotifications) {
				showError(
					err instanceof Error
						? err.message
						: "Failed to unlock shipment. Please try again.",
				);
			}
		},
	});

	return {
		unlockShipment: mutate,
		unlockShipmentAsync: mutateAsync,
		isUnlockingShipment: isPending,
		error,
		data,
		resetError: reset,
	};
};
